import type {
  CheckboxGroupFieldDefinition,
  FormbrewSubmissionMetadata,
  FormbrewSubmissionValues,
  NumberValidation,
} from "./types.ts";

export { getConfirmValidationMessage, synchronizeConfirmFields } from "./renderer.confirm.ts";

export type NumberInputConstraints = {
  min?: number;
  max?: number;
  step: number | "any";
};

/** Native constraints must not exclude server-valid numbers due to the step base. */
export const getNumberInputConstraints = (
  rules: NumberValidation | null,
): NumberInputConstraints => {
  let min = rules?.min;

  if (rules?.allowNegative === false) {
    min = Math.max(0, min ?? 0);
  }

  let places = rules?.maxDecimalPlaces;

  if (rules?.integer) {
    places = 0;
  }

  let step: number | "any" = "any";

  if (places !== undefined && (min === undefined || Number.isInteger(min * 10 ** places))) {
    step = 10 ** -places;
  }

  return { min, max: rules?.max, step };
};

/** Submission metadata or a callback that supplies it at submission time. */
export type FormbrewMetadataProvider =
  | FormbrewSubmissionMetadata
  | (() => FormbrewSubmissionMetadata);

/** Form values and underscore-prefixed metadata collected from a form element. */
export type CollectedSubmission = {
  metadata: FormbrewSubmissionMetadata;
  values: FormbrewSubmissionValues;
};

type SubmissionValueTarget = FormbrewSubmissionMetadata | FormbrewSubmissionValues;

/** Collects string form values, moving underscore-prefixed entries into metadata. */
export const collectFormbrewSubmission = (form: HTMLFormElement): CollectedSubmission => {
  const metadata = Object.create(null) as FormbrewSubmissionMetadata;
  const values = Object.create(null) as FormbrewSubmissionValues;

  for (const [rawKey, rawValue] of new FormData(form)) {
    if (typeof rawValue !== "string") {
      continue;
    }

    let key = rawKey;
    let target: SubmissionValueTarget = values;

    if (rawKey.startsWith("_")) {
      key = rawKey.slice(1);
      target = metadata;
    }

    appendSubmissionValue(target, key, rawValue);
  }

  return { metadata, values };
};

/** Resolves static or callback-provided submission metadata. */
export const resolveFormbrewMetadata = (
  metadata: FormbrewMetadataProvider | undefined,
): FormbrewSubmissionMetadata => {
  if (!metadata) {
    return {};
  }

  if (typeof metadata === "function") {
    return metadata();
  }

  return metadata;
};

/** Returns the checkbox-group constraint message for the current selection count. */
export const getCheckboxGroupValidationMessage = (
  field: CheckboxGroupFieldDefinition,
  selectedCount: number,
): string => {
  const requiredMinimum = field.required ? 1 : 0;
  const configuredMinimum = field.validationJson?.minSelections ?? 0;
  const minimum = Math.max(requiredMinimum, configuredMinimum);
  const maximum = field.validationJson?.maxSelections;

  if (selectedCount < minimum) {
    const suffix = minimum === 1 ? "" : "s";

    return `Select at least ${minimum} option${suffix}.`;
  }

  if (maximum !== undefined && selectedCount > maximum) {
    const suffix = maximum === 1 ? "" : "s";

    return `Select no more than ${maximum} option${suffix}.`;
  }

  return "";
};

const appendSubmissionValue = (target: SubmissionValueTarget, key: string, value: string): void => {
  let current = target[key];

  if (!Object.hasOwn(target, key)) {
    current = undefined;
  }

  if (current === undefined) {
    target[key] = value;

    return;
  }

  if (Array.isArray(current) && current.every((item) => typeof item === "string")) {
    target[key] = [...current, value];

    return;
  }

  if (typeof current === "string") {
    target[key] = [current, value];
  }
};
